import { Component, ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';

interface FormErrorBoundaryProps {
  children: ReactNode;
  onClose: () => void;
}

interface FormErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class FormErrorBoundary extends Component<FormErrorBoundaryProps, FormErrorBoundaryState> {
  constructor(props: FormErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): FormErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Form error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 4,
            minHeight: 400,
          }}
        >
          <Typography variant='h5' gutterBottom color='error'>
            Form Error
          </Typography>
          <Typography variant='body1' color='text.secondary' gutterBottom>
            An unexpected error occurred while loading the form.
          </Typography>
          {this.state.error && (
            <Typography variant='body2' color='text.secondary' sx={{ mt: 2, fontFamily: 'monospace' }}>
              {this.state.error.message}
            </Typography>
          )}
          <Button variant='contained' color='secondary' onClick={this.props.onClose} sx={{ mt: 3 }}>
            Close
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}
